// Consultório (0.24): para médicos, fisioterapeutas, nutricionistas, fonoaudiólogos, psicólogos e outros
// profissionais de saúde. Pacientes, atendimentos com evolução (no cofre) e prescrição, modelos, financeiro
// com convênio, recibo e declaração para reembolso em PDF e a lista do mês para o Receita Saúde.
import { CartaoApoio } from '../components/Recolhivel';
import { useReducer } from 'react';
import { consultaNoPlanner } from './agendaAuto';
import { Alert, Pressable, Text, View } from 'react-native';
import { copiarParaClientes } from '../lib/clientes';
import type { Reg } from '../store/types';
import type { Colecao, Ctx, ModDef, PainelProps } from './tipos';
import { dataBr, din, dinDe, dinSomas, junta, moedaDo, num, op, somaPorMoeda, temNum, temSoma } from './util';
import { mudarCampos, regsDe } from '../store/modulos';
import { whatsapp } from '../lib/mapa';
import { compartilharAnexo, compartilharTexto } from '../lib/anexos';
import { compartilharCsv, gerarECompartilharPdf, reaisPorExtenso, type BlocoPdf } from '../lib/pdf';
import { C, alpha } from '../components/theme';
import { longDate, today, horaTela } from '../lib/date';
import { N_, t } from '../lib/i18n';
import { colecoesOndeTrabalho } from './ondeTrabalho';
import { ATENDIMENTO, PLANTAO, SALARIO } from './ondeTrabalhoBase';

const CONSELHOS = ['CRM', 'CRO', 'CREFITO', 'CRN', 'CRFa', 'CRP', 'COREN', 'CREF', 'CRBM', 'CRF', N_('Outro')];
const TIPOS = [N_('Consulta'), N_('Retorno'), N_('Sessão'), N_('Avaliação'), N_('Procedimento'), N_('Teleconsulta'), N_('Visita domiciliar')];
const FORMAS = [N_('PIX'), N_('Dinheiro'), N_('Cartão de débito'), N_('Cartão de crédito'), N_('Convênio'), N_('Transferência'), N_('Cortesia')];

const perfilDe = (ctx: Ctx) => ctx.regs.find(r => r.col === 'perfil') ?? null;
const registro = (p: Reg | null) => (p ? [p.c.conselho && p.c.conselho !== 'Outro' ? p.c.conselho : p.c.conselhoOutro, p.c.numero && `${p.c.numero}${p.c.uf ? '/' + p.c.uf : ''}`].filter(Boolean).join(' ') : '');
const ehBRL = (moeda: string) => moeda === 'BRL';

function assinatura(p: Reg | null): BlocoPdf[] {
  if (!p) return [{ t: 'assinatura', linhas: [t('Assinatura do profissional')] }];
  return [{ t: 'assinatura', linhas: [p.c.nome || '', junta(p.c.especialidade, registro(p)), junta(p.c.cpf && 'CPF ' + p.c.cpf, p.c.cnpj && 'CNPJ ' + p.c.cnpj),
    junta(p.c.endereco, p.c.telefone)].filter(Boolean) }];
}
const cabecalho = (p: Reg | null): BlocoPdf[] => (p ? [{ t: 'sub', texto: junta(p.c.razao || p.c.nome, registro(p), p.c.telefone, p.c.email) }, { t: 'linha' }] : []);

async function recibo(ctx: Ctx, a: Reg) {
  const pac = ctx.porId(a.pai), p = perfilDe(ctx);
  if (!p) return Alert.alert(t('Complete seus dados'), t('Cadastre seus dados profissionais (nome, conselho e número) em "Meus dados" para sair no recibo.'));
  const pagador = pac?.c.pagador || pac?.c.nome || '';
  const cpfPag = pac?.c.pagador ? pac?.c.cpfPagador : pac?.c.cpf;
  const valor = num(temNum(a.c.valorPago) ? a.c.valorPago : a.c.valor), md = moedaDo(a.c, 'valor', ctx.moeda);
  const cidade = p.c.cidade || '';
  const texto = t('Recebi de {pagador}{doc}, a quantia de {valor}{extenso}, referente a {tipo} realizado(a) em {data}{paciente}.', {
    pagador, doc: cpfPag ? `, CPF ${cpfPag}` : '', valor: din(valor, md), extenso: ehBRL(md) ? ` (${reaisPorExtenso(valor)})` : '',
    tipo: op(a.c.tipo || 'Consulta').toLowerCase(), data: dataBr(a.c.data),
    paciente: pac?.c.pagador ? t(' ao(à) paciente {nome}{cpf}', { nome: pac.c.nome, cpf: pac.c.cpf ? `, CPF ${pac.c.cpf}` : '' }) : '' });
  await gerarECompartilharPdf(`${t('Recibo')} ${pac?.c.nome ?? ''} ${a.c.data}`, [
    ...cabecalho(p),
    { t: 'espaco', h: 20 }, { t: 'titulo', texto: t('RECIBO') }, { t: 'par', texto: din(valor, md), negrito: true, centro: true, tam: 14 }, { t: 'espaco', h: 16 },
    { t: 'par', texto }, { t: 'par', texto: a.c.forma ? t('Forma de pagamento: {f}.', { f: op(a.c.forma) }) : '' },
    { t: 'par', texto: t('Para clareza, firmo o presente.') }, { t: 'espaco', h: 10 },
    { t: 'par', texto: junta(cidade, longDate(a.c.pagoEm || a.c.data)), centro: true },
    ...assinatura(p),
  ], 'My Planner');
  mudarCampos(ctx.mod, a.id, { recibo: true });
}

async function declaracao(ctx: Ctx, a: Reg) {
  const pac = ctx.porId(a.pai), p = perfilDe(ctx);
  if (!p) return Alert.alert(t('Complete seus dados'), t('Cadastre seus dados profissionais (nome, conselho e número) em "Meus dados" para sair no documento.'));
  const valor = num(temNum(a.c.valorPago) ? a.c.valorPago : a.c.valor);
  await gerarECompartilharPdf(`${t('Declaração de atendimento')} ${pac?.c.nome ?? ''} ${a.c.data}`, [
    ...cabecalho(p),
    { t: 'espaco', h: 20 }, { t: 'titulo', texto: t('DECLARAÇÃO DE ATENDIMENTO') }, { t: 'espaco', h: 10 },
    { t: 'par', texto: t('Declaro, para fins de reembolso{conv}, que o(a) paciente {nome}{cpf}{cart} foi atendido(a) por mim em {data}{hora}, em {tipo}.', {
      conv: pac?.c.convenio ? t(' junto ao plano de saúde {c}', { c: pac.c.convenio }) : '', nome: pac?.c.nome ?? '', cpf: pac?.c.cpf ? `, CPF ${pac.c.cpf}` : '',
      cart: pac?.c.carteirinha ? t(', carteirinha nº {n},', { n: pac.c.carteirinha }) : '', data: dataBr(a.c.data), hora: a.c.hora ? t(' às {h}', { h: horaTela(a.c.hora) }) : '',
      tipo: op(a.c.tipo || 'Consulta').toLowerCase() }) },
    ...(a.c.codigo ? [{ t: 'par' as const, texto: t('Procedimento: {c}', { c: a.c.codigo }) }] : []),
    ...(a.c.cid ? [{ t: 'par' as const, texto: t('CID: {c}', { c: a.c.cid }) }] : []),
    { t: 'par', texto: t('Valor do atendimento: {v}{pago}.', { v: dinDe(a, 'valor', ctx.moeda, valor), pago: a.c.pago ? t(', pago em {d}{f}', { d: dataBr(a.c.pagoEm || a.c.data), f: a.c.forma ? ` (${op(a.c.forma)})` : '' }) : '' }) },
    { t: 'espaco', h: 10 },
    { t: 'par', texto: junta(p.c.cidade, longDate(today())), centro: true },
    ...assinatura(p),
  ], 'My Planner');
}

const mesAtual = () => today().slice(0, 7);
const mudarMes = (mes: string, d: number) => { const [a, m] = mes.split('-').map(Number); const x = new Date(a, m - 1 + d, 1); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}`; };
const nomeMes = (mes: string) => { const [a, m] = mes.split('-').map(Number); return new Date(a, m - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' }); };
let mesFin = mesAtual();

function atendimentosDoMes(ctx: Ctx, mes: string) {
  return regsDe(ctx.mod).filter(r => r.col === 'atendimento' && String(r.c.data ?? '').startsWith(mes)).sort((a, b) => String(a.c.data).localeCompare(b.c.data));
}

/** Lista para lançar no Receita Saúde (Carnê-Leão): quem pagou, quem foi atendido, data e valor. */
async function exportarReceitaSaude(ctx: Ctx, mes: string) {
  const pagos = regsDe(ctx.mod).filter(r => r.col === 'atendimento' && r.c.pago && r.c.forma !== 'Cortesia' && String(r.c.pagoEm || r.c.data).startsWith(mes));
  if (!pagos.length) return Alert.alert(t('Nada no mês'), t('Nenhum atendimento pago neste mês.'));
  const linhas = pagos.map(a => {
    const pac = ctx.porId(a.pai);
    return [a.c.pagoEm || a.c.data, pac?.c.pagador || pac?.c.nome || '', pac?.c.pagador ? pac?.c.cpfPagador || '' : pac?.c.cpf || '', pac?.c.nome || '', pac?.c.cpf || '',
      num(temNum(a.c.valorPago) ? a.c.valorPago : a.c.valor).toFixed(2).replace('.', ','), op(a.c.forma), op(a.c.tipo)];
  });
  await compartilharCsv(`receita-saude-${mes}`, [[t('Data do pagamento'), t('Pagador'), t('CPF do pagador'), t('Paciente'), t('CPF do paciente'), t('Valor'), t('Forma'), t('Atendimento')], ...linhas]);
}

function Financeiro({ ctx }: { ctx: Ctx }) {
  const [, re] = useReducer((n: number) => n + 1, 0);
  const l = atendimentosDoMes(ctx, mesFin).filter(a => a.c.status !== 'Faltou' && a.c.status !== 'Desmarcado');
  const valor = (a: Reg) => num(temNum(a.c.valorPago) ? a.c.valorPago : a.c.valor);
  // 0.69 (B1): cada atendimento pode ter a sua moeda; os totais saem separados por moeda
  const recebido = somaPorMoeda(l.filter(a => a.c.pago), valor, ctx.moeda);
  const aReceber = somaPorMoeda(l.filter(a => !a.c.pago && a.c.forma !== 'Cortesia'), a => num(a.c.valor), ctx.moeda);
  const porForma = new Map<string, Reg[]>();
  l.filter(a => a.c.pago).forEach(a => porForma.set(a.c.forma || 'Outro', [...(porForma.get(a.c.forma || 'Outro') ?? []), a]));
  const faltas = atendimentosDoMes(ctx, mesFin).filter(a => a.c.status === 'Faltou').length;
  const perfil = perfilDe(ctx);
  return (
    <View style={{ backgroundColor: C.card, borderRadius: 14, padding: 12, gap: 6 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <Pressable hitSlop={10} onPress={() => { mesFin = mudarMes(mesFin, -1); re(); }}><Text style={{ color: C.sub, fontSize: 20 }}>‹</Text></Pressable>
        <Text style={{ color: C.text, fontWeight: '800', flex: 1, textAlign: 'center' }}>💰 {nomeMes(mesFin)}</Text>
        <Pressable hitSlop={10} onPress={() => { mesFin = mudarMes(mesFin, 1); re(); }}><Text style={{ color: C.sub, fontSize: 20 }}>›</Text></Pressable>
      </View>
      <Text style={{ color: C.text }}>{t('{n} atendimento(s)', { n: l.length })}{faltas ? <Text style={{ color: C.warn }}> · {t('{n} falta(s)', { n: faltas })}</Text> : null}</Text>
      <Text style={{ color: C.ok, fontWeight: '700' }}>{t('Recebido: {v}', { v: dinSomas(recebido, ctx.moeda) })}</Text>
      {temSoma(aReceber) && <Text style={{ color: C.warn, fontWeight: '700' }}>{t('A receber: {v}', { v: dinSomas(aReceber, ctx.moeda) })}</Text>}
      {[...porForma.entries()].map(([f, v]) => <Text key={f} style={{ color: C.sub, fontSize: 12 }}>{op(f)}: {dinSomas(somaPorMoeda(v, valor, ctx.moeda), ctx.moeda)}</Text>)}
      <Pressable onPress={() => exportarReceitaSaude(ctx, mesFin).catch(e => Alert.alert(t('Não foi possível'), String(e?.message ?? e)))}
        style={{ backgroundColor: alpha(ctx.cor, 0.16), borderRadius: 10, padding: 10, alignItems: 'center', marginTop: 4 }}>
        <Text style={{ color: ctx.cor, fontWeight: '800' }}>📑 {t('Planilha do mês para o Receita Saúde')}</Text>
      </Pressable>
      <Text style={{ color: C.mute, fontSize: 11 }}>{t('Com quem pagou, o paciente, a data e o valor de cada atendimento pago. Como pessoa física, lance cada um no app Receita Federal › Receita Saúde; com CNPJ, emita a nota de serviço.')}</Text>
      {!perfil && <Text style={{ color: C.warn, fontSize: 12 }}>{t('Cadastre "Meus dados" para os recibos saírem com seu nome, conselho e número.')}</Text>}
    </View>
  );
}

function PainelPacientes({ ctx, lista }: PainelProps) {
  const hoje = today();
  const doDia = regsDe(ctx.mod).filter(r => r.col === 'atendimento' && r.c.data === hoje).sort((a, b) => String(a.c.hora ?? '').localeCompare(b.c.hora ?? ''));
  return (
    <View style={{ gap: 10 }}>
      {!!doDia.length && (
        <CartaoApoio titulo={'📅 ' + t('Hoje')} chave={'cons:hoje'} resumo={String(doDia.length)} gap={4}>
          {doDia.map(a => <Text key={a.id} style={{ color: C.text }}>{a.c.hora ? horaTela(a.c.hora) : '--:--'} · {ctx.porId(a.pai)?.c.nome ?? ''} · {op(a.c.tipo)}{a.c.pago ? ' ✅' : ''}</Text>)}
        </CartaoApoio>
      )}
      {(lista.length > 0 || !!perfilDe(ctx)) && <Financeiro ctx={ctx} />}
    </View>
  );
}

const perfil: Colecao = {
  id: 'perfil', nome: N_('Meus dados'), um: N_('Meus dados profissionais'), icone: '🪪',
  campos: [
    { k: 'nome', r: N_('Nome completo'), t: 'texto' },
    { k: 'especialidade', r: N_('Profissão ou especialidade'), t: 'texto', ph: N_('Ex.: Fisioterapeuta, Nutricionista, Cardiologista') },
    { k: 'conselho', r: N_('Conselho'), t: 'opcao', op: CONSELHOS },
    { k: 'conselhoOutro', r: N_('Nome do conselho'), t: 'texto', mostrar: c => c.conselho === 'Outro' },
    { k: 'numero', r: N_('Número do registro'), t: 'texto' },
    { k: 'uf', r: N_('Estado (UF) do registro'), t: 'texto', ph: 'PR' },
    { k: 'cpf', r: N_('CPF'), t: 'texto' },
    { k: 'cnpj', r: N_('CNPJ (se tiver)'), t: 'texto' },
    { k: 'razao', r: N_('Razão social ou nome do consultório'), t: 'texto' },
    { k: 'endereco', r: N_('Endereço do consultório'), t: 'endereco' },
    { k: 'cidade', r: N_('Cidade (para a data dos recibos)'), t: 'texto' },
    { k: 'telefone', r: N_('Telefone'), t: 'telefone' },
    { k: 'email', r: N_('E-mail'), t: 'email' },
    { k: 'valor', r: N_('Valor padrão da consulta ou sessão'), t: 'dinheiro' },
    { k: 'notas', r: N_('Observações (dados bancários, PIX...)'), t: 'longo', seg: 'fin' },
  ],
  titulo: r => r.c.nome || t('Meus dados profissionais'),
  sub: r => junta(r.c.especialidade, registro(r)),
  vazio: N_('Cadastre seu nome, conselho e número do registro. Eles saem nos recibos e nas declarações.'),
};

const paciente: Colecao = {
  id: 'paciente', nome: N_('Pacientes'), um: N_('Paciente'), anexos: true, ordem: 'titulo',
  icone: r => (r.c.ativo === false ? '💤' : '🧑'),
  campos: [
    { k: 'nome', r: N_('Nome completo'), t: 'texto' },
    { k: 'cpf', r: N_('CPF'), t: 'texto' },
    { k: 'nascimento', r: N_('Data de nascimento'), t: 'data' },
    { k: 'telefone', r: N_('Telefone'), t: 'telefone' },
    { k: 'email', r: N_('E-mail'), t: 'email' },
    { k: 'endereco', r: N_('Endereço'), t: 'endereco' },
    { k: 'pagador', r: N_('Quem paga, se não for o paciente (mãe, pai, responsável)'), t: 'texto' },
    { k: 'cpfPagador', r: N_('CPF de quem paga'), t: 'texto', mostrar: c => !!c.pagador },
    { k: 'convenio', r: N_('Plano de saúde'), t: 'texto' },
    { k: 'carteirinha', r: N_('Número da carteirinha'), t: 'texto', mostrar: c => !!c.convenio },
    { k: 'valor', r: N_('Valor combinado por atendimento'), t: 'dinheiro' },
    { k: 'anamnese', r: N_('Anamnese e histórico (no cofre)'), t: 'cofre', seg: 'clin' },
    { k: 'alertas', r: N_('Alergias e alertas'), t: 'texto', seg: 'clin' },
    { k: 'ativo', r: N_('Em tratamento'), t: 'sim' },
    { k: 'notas', r: N_('Observações'), t: 'longo' },
  ],
  titulo: r => r.c.nome || '',
  sub: (r, ctx) => {
    const at = ctx.filhos(r.id, 'atendimento');
    const ult = at.map(a => a.c.data).filter(Boolean).sort().pop();
    return junta(r.c.convenio, at.length && t('{n} atendimento(s)', { n: at.length }), ult && t('último em {d}', { d: dataBr(ult) }), r.c.alertas && '⚠️ ' + r.c.alertas);
  },
  novo: ctx => ({ ativo: true, valor: perfilDe(ctx)?.c.valor ?? '' }),
  filtros: [[N_('Em tratamento'), r => r.c.ativo !== false], [N_('Com convênio'), r => !!r.c.convenio], [N_('Com valor a receber'), (r, ctx) => ctx.filhos(r.id, 'atendimento').some(a => !a.c.pago && a.c.forma !== 'Cortesia' && num(a.c.valor) > 0)]],
  acoes: r => [...(r.c.telefone ? [{ label: '💬 WhatsApp', onPress: () => whatsapp(r.c.telefone, '') }] : []),
    // texto 25 (A3): só nome e contato (nunca prontuário, evolução ou dados de saúde)
    { label: '👥 ' + t('Copiar para Meus clientes'), onPress: () => copiarParaClientes({ nome: r.c.nome, telefone: r.c.telefone, email: r.c.email }) }],
  painel: PainelPacientes,
  vazio: N_('Cadastre seus pacientes. Dentro de cada um ficam os atendimentos com evolução, prescrição, pagamento, recibo e declaração para o plano de saúde.'),
};

const atendimento: Colecao = {
  id: 'atendimento', pai: 'paciente', nome: N_('Atendimentos'), um: N_('Atendimento'), data: 'data', ordem: 'data-desc', anexos: true, check: 'pago',
  pagamento: { tipo: 'receber', venc: 'data', data: 'pagoEm', valorPago: 'valorPago', forma: 'forma' },
  // texto 28 (C3): o "Enviar para o My Planner" desta lista leva só os valores a receber (a consulta marcada já vai sozinha)
  aReceberNoPlanner: { data: 'data', rotulo: N_('A receber'), incluir: r => !r.c.pago && Number(r.c.valor) > 0 && r.c.forma !== 'Cortesia' && r.c.status !== 'Desmarcado' && r.c.status !== 'Faltou' },
  icone: r => (r.c.status === 'Faltou' ? '🚫' : r.c.status === 'Desmarcado' ? '↩️' : r.c.pago ? '✅' : '🩺'),
  campos: [
    { k: 'data', r: N_('Data'), t: 'data' },
    { k: 'hora', r: N_('Horário'), t: 'hora' },
    { k: 'tipo', r: N_('Tipo'), t: 'opcao', op: TIPOS },
    { k: 'status', r: N_('Situação'), t: 'opcao', op: [N_('Agendado'), N_('Realizado'), N_('Faltou'), N_('Desmarcado')] },
    { k: 'queixa', r: N_('Queixa principal'), t: 'texto', seg: 'clin' },
    { k: 'evolucao', r: N_('Evolução (no cofre)'), t: 'cofre', seg: 'clin' },
    { k: 'prescricao', r: N_('Prescrição, conduta e orientações'), t: 'longo', seg: 'clin' },
    { k: 'codigo', r: N_('Código do procedimento (TUSS ou do plano)'), t: 'texto' },
    { k: 'cid', r: N_('CID (se o paciente pedir na declaração)'), t: 'texto', seg: 'clin' },
    { k: 'valor', r: N_('Valor'), t: 'dinheiro' },
    { k: 'forma', r: N_('Forma de pagamento'), t: 'opcao', op: FORMAS, seg: 'fin' },
    { k: 'guia', r: N_('Número da guia do convênio'), t: 'texto', mostrar: c => c.forma === 'Convênio', seg: 'fin' },
    { k: 'pago', r: N_('Pago'), t: 'sim', seg: 'fin' },
    { k: 'pagoEm', r: N_('Data do pagamento'), t: 'data', mostrar: c => !!c.pago, seg: 'fin' },
    { k: 'valorPago', r: N_('Valor pago'), t: 'dinheiro', mostrar: c => !!c.pago },
    { k: 'recibo', r: N_('Recibo emitido'), t: 'sim' },
  ],
  titulo: r => junta(op(r.c.tipo || 'Consulta'), dataBr(r.c.data)),
  sub: (r, ctx) => junta(horaTela(r.c.hora), r.c.status && r.c.status !== 'Realizado' && op(r.c.status), r.c.queixa, temNum(r.c.valor) && dinDe(r, 'valor', ctx.moeda),
    r.c.pago ? t('pago') : r.c.forma === 'Cortesia' ? op('Cortesia') : temNum(r.c.valor) && t('a receber'), r.c.recibo && '🧾'),
  novo: (ctx, pai) => ({ data: today(), tipo: 'Consulta', status: 'Realizado', valor: pai?.c.valor ?? perfilDe(ctx)?.c.valor ?? '', forma: pai?.c.convenio ? 'Convênio' : 'PIX' }),
  ajustar: (c, k) => (k === 'pago' ? { ...c, pagoEm: c.pago ? (c.pagoEm || today()) : '', valorPago: c.pago ? (temNum(c.valorPago) ? c.valorPago : c.valor) : c.valorPago } : c),
  agenda: (r, ctx) => ({ start: r.c.data, time: r.c.hora || null, title: t('{tipo}: {p}', { tipo: op(r.c.tipo || 'Consulta'), p: ctx.porId(r.pai)?.c.nome ?? '' }), duration: 50 }),
  // 0.65 (Texto 3 C): consulta marcada (com data, e não desmarcada) vai sozinha para o My Planner
  aoSalvar: (r, ctx) => { if (r.c.status !== 'Desmarcado' && r.c.status !== 'Faltou' && (!r.c.data || r.c.data >= today())) consultaNoPlanner('consultorio', r, { start: r.c.data, time: r.c.hora || null, title: t('{tipo}: {p}', { tipo: op(r.c.tipo || 'Consulta'), p: ctx.porId(r.pai)?.c.nome ?? '' }) }); },
  acoes: (r, ctx) => {
    const modelos = ctx.regs.filter(x => x.col === 'modelo' && x.c.texto);
    const pac = ctx.porId(r.pai);
    return [
      { label: '🧾 ' + t('Recibo em PDF'), onPress: () => { recibo(ctx, r).catch(e => Alert.alert(t('Não foi possível'), String(e?.message ?? e))); } },
      { label: '📄 ' + t('Declaração para reembolso (PDF)'), onPress: () => { declaracao(ctx, r).catch(e => Alert.alert(t('Não foi possível'), String(e?.message ?? e))); } },
      ...(!r.c.pago ? [{ label: '💰 ' + t('Marcar como pago'), onPress: () => mudarCampos(ctx.mod, r.id, { pago: true, pagoEm: today(), valorPago: r.c.valor }) }] : []),
      ...(pac?.c.telefone && r.c.prescricao ? [{ label: '💬 ' + t('Mandar as orientações pelo WhatsApp'), onPress: () => whatsapp(pac.c.telefone, r.c.prescricao) }] : []),
      ...modelos.slice(0, 8).map(m => ({ label: '📋 ' + t('Usar modelo: {m}', { m: m.c.nome }), onPress: () => mudarCampos(ctx.mod, r.id, { prescricao: [r.c.prescricao, m.c.texto].filter(Boolean).join('\n\n') }) })),
    ];
  },
  filtros: [[N_('A receber'), r => !r.c.pago && r.c.forma !== 'Cortesia' && num(r.c.valor) > 0], [N_('Convênio'), r => r.c.forma === 'Convênio'], [N_('Sem recibo'), r => !!r.c.pago && !r.c.recibo]],
  vazio: N_('Registre cada atendimento: a evolução fica no cofre, com criptografia. A prescrição pode vir de um modelo.'),
};

// ─── Para o paciente (0.65, B14) ───
// Pasta dentro de cada paciente com o que é feito para sair do consultório: orientações, receitas, atestados,
// pedidos de exame e materiais. Tudo o que é enviado daqui sai SÓ desta ficha (campos e anexos dela), com o nome
// do paciente e os seus dados profissionais. O prontuário (anamnese, alertas, queixa, evolução, prescrição, CID)
// nunca é lido aqui; para mandar algo do prontuário, copie o texto para cá de propósito.
const TIPOS_PAC = [N_('Orientação'), N_('Receita médica'), N_('Atestado'), N_('Pedido de exame'), N_('Material'), N_('Outro')];
const ICONE_PAC: Record<string, string> = { 'Orientação': '📝', 'Receita médica': '💊', 'Atestado': '📄', 'Pedido de exame': '🧪', 'Material': '📚' };

/** Texto que vai para o paciente: só os campos desta ficha e a assinatura do profissional. */
function textoParaPaciente(ctx: Ctx, r: Reg) {
  const pac = ctx.porId(r.pai), p = perfilDe(ctx);
  return [
    junta(op(r.c.tipo), r.c.titulo),
    pac?.c.nome ? t('Paciente: {nome}', { nome: pac.c.nome }) : '',
    r.c.texto ? '\n' + r.c.texto : '',
    r.c.data ? '\n' + dataBr(r.c.data) : '',
    p ? junta(p.c.nome, registro(p)) : '',
  ].filter(Boolean).join('\n');
}

async function pdfParaPaciente(ctx: Ctx, r: Reg) {
  const pac = ctx.porId(r.pai), p = perfilDe(ctx);
  await gerarECompartilharPdf(`${op(r.c.tipo || 'Orientação')} ${pac?.c.nome ?? ''} ${r.c.data ?? ''}`, [
    ...cabecalho(p),
    { t: 'espaco', h: 16 }, { t: 'titulo', texto: op(r.c.tipo || 'Orientação').toUpperCase() },
    ...(r.c.titulo ? [{ t: 'sub' as const, texto: r.c.titulo }] : []),
    { t: 'espaco', h: 8 },
    ...(pac?.c.nome ? [{ t: 'par' as const, texto: t('Paciente: {nome}', { nome: pac.c.nome }), negrito: true }] : []),
    { t: 'espaco', h: 6 },
    ...String(r.c.texto ?? '').split(/\n{2,}/).filter(s => s.trim()).map(s => ({ t: 'par' as const, texto: s })),
    { t: 'espaco', h: 10 },
    { t: 'par', texto: junta(p?.c.cidade, longDate(r.c.data || today())), centro: true },
    ...assinatura(p),
  ], 'My Planner');
}

const marcarEnviado = (ctx: Ctx, r: Reg) => { if (!r.c.enviado) mudarCampos(ctx.mod, r.id, { enviado: true, enviadoEm: today() }); };
const falhou = (e: any) => Alert.alert(t('Não foi possível'), String(e?.message ?? e));

const paraPaciente: Colecao = {
  id: 'parapaciente', pai: 'paciente', nome: N_('Para o paciente'), um: N_('Item para o paciente'), data: 'data', ordem: 'data-desc', anexos: true, check: 'enviado',
  icone: r => ICONE_PAC[r.c.tipo] ?? '📎',
  campos: [
    { k: 'tipo', r: N_('Tipo'), t: 'opcao', op: TIPOS_PAC },
    { k: 'titulo', r: N_('Título'), t: 'texto', ph: N_('Ex.: Exercícios para casa, Atestado de 2 dias') },
    { k: 'data', r: N_('Data'), t: 'data' },
    { k: 'texto', r: N_('Texto para o paciente'), t: 'longo' },
    { k: 'enviado', r: N_('Enviado ao paciente'), t: 'sim' },
    { k: 'enviadoEm', r: N_('Enviado em'), t: 'data', mostrar: c => !!c.enviado },
  ],
  titulo: r => r.c.titulo || op(r.c.tipo) || t('Item para o paciente'),
  sub: r => junta(r.c.titulo && op(r.c.tipo), dataBr(r.c.data), r.anexos?.length && t('{n} arquivo(s)', { n: r.anexos.length }),
    r.c.enviado ? t('enviado em {d}', { d: dataBr(r.c.enviadoEm || r.c.data) }) : t('não enviado')),
  novo: () => ({ tipo: 'Orientação', data: today() }),
  ajustar: (c, k) => (k === 'enviado' ? { ...c, enviadoEm: c.enviado ? (c.enviadoEm || today()) : '' } : c),
  acoes: (r, ctx) => {
    const pac = ctx.porId(r.pai);
    const txt = textoParaPaciente(ctx, r);
    const modelos = ctx.regs.filter(x => x.col === 'modelo' && x.c.texto);
    return [
      ...(pac?.c.telefone ? [{ label: '💬 ' + t('Mandar ao paciente pelo WhatsApp'), onPress: () => { whatsapp(pac.c.telefone, txt); marcarEnviado(ctx, r); } }] : []),
      { label: '📤 ' + t('Compartilhar o texto'), onPress: () => { compartilharTexto(txt, r.c.titulo || op(r.c.tipo)).then(() => marcarEnviado(ctx, r)).catch(() => {}); } },
      { label: '📄 ' + t('PDF com seus dados e assinatura'), onPress: () => { pdfParaPaciente(ctx, r).then(() => marcarEnviado(ctx, r)).catch(falhou); } },
      // os arquivos desta ficha (um por vez, pela janela de compartilhar do celular)
      ...(r.anexos ?? []).map(a => ({ label: '📎 ' + t('Enviar o arquivo: {nome}', { nome: a.nome || t('arquivo') }), onPress: () => { compartilharAnexo(a).then(() => marcarEnviado(ctx, r)).catch(falhou); } })),
      ...modelos.slice(0, 8).map(m => ({ label: '📋 ' + t('Usar modelo: {m}', { m: m.c.nome }), onPress: () => mudarCampos(ctx.mod, r.id, { texto: [r.c.texto, m.c.texto].filter(Boolean).join('\n\n') }) })),
    ];
  },
  filtros: [[N_('Não enviados'), r => !r.c.enviado], [N_('Receitas médicas'), r => r.c.tipo === 'Receita médica'], [N_('Atestados'), r => r.c.tipo === 'Atestado']],
  vazio: N_('Orientações, receitas, atestados e materiais para entregar ao paciente, com arquivos. Só o que está aqui é enviado: o prontuário nunca sai por esta pasta.'),
};

const modelo: Colecao = {
  id: 'modelo', nome: N_('Modelos'), um: N_('Modelo de prescrição'), icone: '📋', ordem: 'titulo',
  campos: [
    { k: 'nome', r: N_('Nome do modelo'), t: 'texto', ph: N_('Ex.: Alongamento lombar, Dieta 1.800 kcal') },
    { k: 'texto', r: N_('Texto'), t: 'longo' },
  ],
  titulo: r => r.c.nome || '',
  sub: r => String(r.c.texto ?? '').split('\n')[0].slice(0, 80),
  vazio: N_('Guarde as prescrições e orientações que você usa sempre. No atendimento, toque em ⋮ › Usar modelo.'),
};

// D4 (Onde trabalho): hospitais e clínicas que pagam você (salário fixo, plantão ou atendimento)
const ondeTrabalho = colecoesOndeTrabalho({ tipos: [N_('Hospital'), N_('Clínica'), N_('Outro')], pagamentos: [SALARIO, PLANTAO, ATENDIMENTO],
  ph: N_('Ex.: Hospital Santa Casa, Clínica Vida'),
  vazio: N_('Hospitais e clínicas que pagam você. Com salário fixo, o app gera o a receber de todo mês; por plantão ou atendimento, marque os feitos e o app calcula.') });

export const CONSULTORIO: ModDef = { id: 'consultorio', colecoes: [paciente, atendimento, paraPaciente, modelo, perfil, ...ondeTrabalho],
  dica: N_('Para profissionais de saúde: pacientes, evolução no cofre, prescrições com modelos, financeiro, recibo e declaração para reembolso em PDF.') };
